<template>
  <section class="osk-cart-orders">
    <div class="osk-wrapper">
      <CartEmpty
        v-if="!cartOrdersLength && !unfinishedItems?.length"
        :is-auth="isAuth"
        class="osk-cart-orders__nothing"
      />

      <div
        v-if="orders?.length"
        class="osk-cart-orders__order-wrap"
      >
        <UikitTabs
          v-if="unfinishedItems?.length && cartOrdersLength"
          v-model="activeTab"
          class="osk-cart-orders__tabs"
        >
          <UikitTabsTab
            :name="CartTab.Ready"
            :title="t('components.cart.cartOrders.tabs.ready', { count: cartOrders.length })"
            class="osk-cart-orders__tab"
            @click="handleTabClick(CartTab.Ready)"
          />

          <UikitTabsTab
            :name="CartTab.Unfinished"
            class="osk-cart-orders__tab"
            @click="handleTabClick(CartTab.Unfinished)"
          >
            <i18n-t keypath="components.cart.cartOrders.tabs.unfinished">
              <template #orders>
                <span class="no-mobile">{{ $t('components.cart.cartOrders.tabs.unfinished.orders') }}</span>
              </template>
              <template #count>
                {{ unfinishedItems?.length ?? 0 }}
              </template>
            </i18n-t>
          </UikitTabsTab>
        </UikitTabs>

        <i18n-t
          v-if="unfinishedItems?.length && !cartOrdersLength"
          tag="div"
          keypath="components.cart.cartOrders.unfinished"
          class="osk-cart-orders__tab-alone"
        >
          <template #orders>
            <span class="no-mobile">{{ $t('components.cart.cartOrders.unfinished.orders') }}</span>
          </template>
          <template #count>
            {{ unfinishedItems?.length ?? 0 }}
          </template>
        </i18n-t>

        <CartOrder
          v-for="(order, index) in orders"
          :key="index"
          class="osk-cart-orders__order"
          mode="cart"
          :is-unfinished="isUnfinishedTab"
          :data="order"
          full
          hide-promo
          :button-text="$t('components.cart.cartOrders.btnCheckount')"
          :loading-id="cartItemLoadingId"
          @apply="handleOrderApply"
          @delete-order="handleDeleteOrder"
          @open-size-count-dialog="openSizeCountDialog"
        />
      </div>
    </div>

    <ProductSizeCountDialog
      v-model="sizeCountDialog"
      :product="currentProduct"
      :size="currentSize"
      @open-size-dialog="openSizeInfoDialog"
    />

    <ProductSizesInfoDialog
      v-model="isShowSizeDialog"
      v-model:size="currentSize"
      :product="currentProduct"
    />
  </section>
</template>

<script setup lang="ts">
import { useCart } from '~/services/cart';
import { OrderDTO, OrderPositionDTORes, SizeValueDTORes } from '~/restAPI/data-contracts';
import { CartTab } from '~/types/cart';
import { AnalyticsEvents } from '~/types/analytics';

const props = defineProps<{
  unfinishedItems?: OrderDTO[] | null,
}>();

const emits = defineEmits<{
  (e: 'update:unfinishedItems', orders: OrderDTO[] | undefined): void,
}>();

const { t } = useI18n();
const { cartOrders } = useCartStore();
const {
  isAuth,
  openAuthDialog,
} = useAccountStore();
const { deleteOrder } = useCart();
const { $addEvent } = useNuxtApp();
const { showErrorDialog } = useErrorDialog();

const cartItemLoadingId = ref<number>();
const loginDialog = ref(false);
const sizeCountDialog = ref(false);
const currentOrder = ref<OrderDTO>();
const currentProduct = ref<OrderPositionDTORes>();
const currentSize = ref<SizeValueDTORes>();
const isShowSizeDialog = ref(false);

const activeTab = ref(props.unfinishedItems?.length ? CartTab.Unfinished : CartTab.Ready);

const cartOrdersLength = computed(() => cartOrders.value.length);
const isUnfinishedTab = computed(() => activeTab.value === CartTab.Unfinished);
const orders = computed(() => (isUnfinishedTab.value ? props.unfinishedItems : cartOrders.value));

function handleTabClick(tab: CartTab) {
  $addEvent(tab === CartTab.Ready ? AnalyticsEvents.CartTabReady : AnalyticsEvents.CartTabUnfinished);
}

async function handleDeleteOrder(orderId?: number) {
  if (!orderId) return;

  cartItemLoadingId.value = orderId;

  const { data, error } = await useAsyncData('deleteOrderPageCart', () => deleteOrder(orderId));

  if (error.value) {
    showErrorDialog({
      title: 'Ошибка при удалении заказа',
      subtitle: error.value?.data?.humanMessage,
    });
  }

  if (data.value) {
    const newOrders = props.unfinishedItems?.filter((order) => order.id !== orderId);

    emits('update:unfinishedItems', newOrders);
  }

  cartItemLoadingId.value = undefined;
}

const nextCheckoutLink = computed(() => `/checkout?sellerId=${currentOrder.value?.seller?.id}${isUnfinishedTab.value ? `&orderId=${currentOrder.value?.id}` : ''}`);

function openLoginDialog(order: OrderDTO) {
  currentOrder.value = order;
  openAuthDialog({
    to: () => nextCheckoutLink.value,
    chapter: 'product_request',
  });
}

function handleOrderApply({ checkoutLink, order }: { checkoutLink: string, order: OrderDTO }) {
  if (!isAuth.value) {
    openLoginDialog(order);
    return;
  }

  navigateTo(checkoutLink);
}

function openSizeCountDialog(product: OrderPositionDTORes) {
  currentProduct.value = product;
  sizeCountDialog.value = true;
}

function openSizeInfoDialog(size?: SizeValueDTORes) {
  currentSize.value = size;
  isShowSizeDialog.value = true;
}

watch(() => cartOrders.value.length, (value) => {
  if (value === 0 && props.unfinishedItems?.length) {
    activeTab.value = CartTab.Unfinished;
  }
});

watch(() => props.unfinishedItems?.length, (value) => {
  if (value === 0) activeTab.value = CartTab.Ready;
});

watch(() => loginDialog.value, (value) => {
  if (!value) currentOrder.value = undefined;
});
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-cart-orders {
  padding-bottom: $indent-large;

  @media screen and (min-width: 580px) {
    padding-bottom: $indent-xlarge;
  }

  &__order-wrap {
    padding-top: $indent-small;
  }

  &__tabs {
    display: inline-flex;
    margin-bottom: $indent-mlarge;

    :deep(.osk-tab__title) {
      padding-right: 0;
      display: block;

      &:first-child {
        margin-left: 0;
      }
    }
  }

  &__tab {
    &:not(:last-child) {
      :deep(.osk-tab__title) {
        @include media-query(md-and-up) {
          padding-right: 40px;
        }

        @include media-query(lg-and-up) {
          padding-right: 100px;
        }
      }
    }
  }

  &__tab-alone {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    margin-bottom: 47px;
    padding-top: $indent-medium;
  }

  &__nothing {
    margin-top: 0;
  }

  &__order {
    &:not(:last-child) {
      padding-bottom: 64px;
    }
  }
}
</style>
